import {APIurl, convertNumbersToBooleans} from "./Utils";
import firebase from "firebase/compat/app";
import {CategoriaModel, ServizioModel, TraduzioniModel, UserModel, UtilsModel} from "./Model";

export const getCategories = async (): Promise<CategoriaModel[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/user/getCategories`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta delle categorie");
        }

        const data = await res.json();
        return data.categories as CategoriaModel[];
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

export const getServices = async (): Promise<ServizioModel[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/user/getServices`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta dei servizi");
        }

        const data = await res.json();

        // Parse coordinates e altri campi JSON se arrivano come stringhe (dipende dal backend)
        const services = (data.services as any[]).map((s) => ({
            ...s,
            coordinates:
                typeof s.coordinates === "string" ? JSON.parse(s.coordinates) : s.coordinates,
            phoneNumbers:
                typeof s.phoneNumbers === "string" ? JSON.parse(s.phoneNumbers) : s.phoneNumbers,
            openingHours:
                typeof s.openingHours === "string" ? JSON.parse(s.openingHours) : s.openingHours,
            website: typeof s.website === "string" ? JSON.parse(s.website) : s.website,
            emailAddress:
                typeof s.emailAddress === "string" ? JSON.parse(s.emailAddress) : s.emailAddress,
        }));

        return services as ServizioModel[];
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

export const addCategory = async (title: string, icon: string): Promise<{ id: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/addCategory`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ title, icon }),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'aggiunta della categoria");
        }

        const data = await res.json();
        return { id: data.id };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const addService = async (service: Omit<ServizioModel, "id">): Promise<{ id: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/addService`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(service),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'aggiunta del servizio");
        }

        const data = await res.json();
        return { id: data.id };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const deleteCategory = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/deleteCategory/${id}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'eliminazione della categoria");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const deleteService = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/deleteService/${id}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'eliminazione del servizio");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const getUsers = async (): Promise<UserModel[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/getUsers`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta degli utenti");
        }

        const data = await res.json();

        return convertNumbersToBooleans(data.users) as UserModel[];
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

export const getTraduzioni = async (): Promise<TraduzioniModel | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/getStringsTranslations`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta delle traduzioni");
        }

        const data = await res.json();

        return data as TraduzioniModel;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const uploadTraduzioni = async (traduzioni: TraduzioniModel): Promise<{ message: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/uploadTranslation`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(traduzioni),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'upload delle traduzioni");
        }

        const data = await res.json();
        return { message: data.message };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const getUtils = async (): Promise<UtilsModel | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/getUtils`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta delle utils");
        }

        const data = await res.json();
        return data as UtilsModel;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const setAppVersion = async (key: string, value: string): Promise<{ message: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/setAppVersion/${key}/${value}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'upload della versione");
        }

        const data = await res.json();
        return { message: data.message };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const setMaintenance = async (value: string): Promise<{ message: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/setMaintenance/${value}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'upload della manutenzione");
        }

        const data = await res.json();
        return { message: data.message };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const editCategoria = async (newData: CategoriaModel): Promise<{ message: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/editCategoria`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(newData),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'edit della categoria");
        }

        const data = await res.json();
        return { message: data.message };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const editServizio = async (newData: ServizioModel): Promise<{ message: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/editServizio`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(newData),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'edit del servizio");
        }

        const data = await res.json();
        return { message: data.message };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const deleteUser = async (id: string): Promise<{ message: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/deleteUser/${id}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'edit del servizio");
        }

        const data = await res.json();
        return { message: data.message };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const editUser = async (newData: UserModel): Promise<{ message: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/editUser`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(newData),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'edit dell'utente");
        }

        const data = await res.json();
        return { message: data.message };
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};